'use client';
import { useMachine, useSelector } from '@xstate/react';
import { createPurchaseSubflow } from '../state';
import {
  TelegramTransactionsUserControllerGetActivePaymentSystems200,
  TelegramTransactionsUserControllerGetTopUpVariants200,
  useTelegramTransactionsUserGetActivePaymentSystems,
  useTelegramTransactionsUserGetTopUpVariants,
} from '@/generated';
import { Loading } from '../../common/Screens/Loading';
import { ChooseAmount } from './ChooseAmount';
import { ChooseMethod } from '../../common/Screens/ChooseMethod';
import { useCallback, useEffect, useMemo } from 'react';
import { services } from '../../common/services';
import { BankPayment } from '../../common/Screens/BankPayment';
import { CardComPayment } from '../../common/Screens/CardComPayment';
import { FinalScreen } from '../../common/Screens/FinalScreen';
import { useUserData } from '@/contexts/LoginContext';
import { useUpdateGlobalUser } from '@/hooks/useUpdateGlobalUser';
import { Error } from '../../common/Screens/Error';
import { runtimeConfig } from '@/config/runtime-config';
import { filterPaymentSystemsForRuntime } from '@/utils/payment-systems';
import { useDelayedFlag } from '@/hooks/useDelayedFlag.hook';

type Props = {
  onClose: () => void;
  minimalAmount?: number;
};

export function PurchaseWidget({ onClose, minimalAmount }: Props) {
  const { data: availableAmounts, isLoading: amountsLoading } = useTelegramTransactionsUserGetTopUpVariants();
  const { data: payments, isLoading: paymentsLoading } = useTelegramTransactionsUserGetActivePaymentSystems();

  const convertedPayments = useMemo(() => {
    return filterPaymentSystemsForRuntime(payments, runtimeConfig.isTelegramMiniApp).filter(
      (payment) => payment.paymentSystem !== 'COINS',
    );
  }, [payments]);

  const convertedAmounts = useMemo(() => {
    if (!minimalAmount) {
      return availableAmounts;
    }
    return availableAmounts?.filter((val) => val.amount > minimalAmount);
  }, [minimalAmount, availableAmounts]);
  const isLoading = amountsLoading || paymentsLoading;
  const showLoading = useDelayedFlag(isLoading);
  return (
    <>
      {isLoading ? (
        showLoading && <Loading onClose={onClose} />
      ) : (
        <PurchaseWidgetInner
          onClose={onClose}
          availableAmounts={convertedAmounts || []}
          payments={convertedPayments || []}
        />
      )}
    </>
  );
}

type InnerProps = {
  onClose: () => void;
  availableAmounts: TelegramTransactionsUserControllerGetTopUpVariants200;
  payments: TelegramTransactionsUserControllerGetActivePaymentSystems200;
};

export function PurchaseWidgetInner({ onClose, availableAmounts, payments }: InnerProps) {
  const machine = useMemo(() => createPurchaseSubflow(services), []);

  const [state, send] = useMachine(machine, { input: { availableAmounts: availableAmounts, methods: payments } });
  return <CoinsBuyingScreen state={state} send={send} onClose={onClose} showBack={false} />;
}

type CoinsBuyingScreenProps = {
  onClose: () => void;
  state: any;
  send: any;
  showBack: boolean;
  onBack?: () => void;
};

export const CoinsBuyingScreenWrapper = ({ actor, onClose, onBack }) => {
  const state = useSelector(actor, (s) => s);
  const send = actor.send;

  return <CoinsBuyingScreen state={state} send={send} onClose={onClose} onBack={onBack} showBack={true} />;
};

export function CoinsBuyingScreen({ onClose, state, send, showBack, onBack }: CoinsBuyingScreenProps) {
  const userData = useUserData();
  const reloadUser = useUpdateGlobalUser();

  useEffect(() => {
    if (state.matches('thankYou')) {
      reloadUser();
    }
  }, [state, reloadUser]);

  const renderStep = useCallback(() => {
    if (state.matches('loadingMethods')) {
      return <Loading onClose={onClose} />;
    }

    if (state.matches('chooseAmount')) {
      return (
        <ChooseAmount
          showBack={showBack}
          onBack={onBack}
          amounts={state.context.availableAmounts || []}
          onSelect={(amt: number) => send({ type: 'SELECT_AMOUNT', amount: amt })}
          onClose={onClose}
        />
      );
    }

    if (state.matches('chooseMethod')) {
      const selectedAmount = state.context.availableAmounts.find((val) => val.amount === state.context.amount);
      return (
        <ChooseMethod
          onClose={onClose}
          fiatAmountToPay={selectedAmount?.fiatAmount || 0}
          telegramStarsAmount={runtimeConfig.isTelegramMiniApp ? selectedAmount?.telegramStarsAmount : undefined}
          showCoinsAlternative={false}
          availableMethods={state.context.methods}
          onBack={() => send({ type: 'BACK' })}
          currencySign={selectedAmount?.currencySign}
          onSelect={(id: string) => send({ type: 'SELECT_METHOD', paymentSystem: id })}
        />
      );
    }

    if (state.matches('cardComDetails')) {
      if (state.context.partsLoading) {
        return <Loading onClose={onClose} />;
      }
      const cardComData = state.context.methods.find((val) => val.paymentSystem === 'CARD_COM');
      const fiatAmount = state.context.availableAmounts.find((val) => val.amount === state.context.amount)?.fiatAmount;

      return (
        <CardComPayment
          name={cardComData?.name || ''}
          amountToPay={fiatAmount}
          tokens={cardComData?.tokens || []}
          parts={state.context.parts || []}
          onBack={() => send({ type: 'BACK' })}
          onGoPay={(partsAmount: number, payerName: string, token?: string) => {
            send({
              type: 'GO_PAY_CARD_COM',
              parts: partsAmount,
              payerName,
              token,
            });
          }}
          onClose={onClose}
        />
      );
    }

    if (state.matches('handleMethod')) {
      return <Loading onClose={onClose} />;
    }

    if (state.matches('manualTransfer')) {
      const selectedAmount = state.context.availableAmounts.find((val) => val.amount === state.context.amount);
      return (
        <BankPayment
          onBack={() => send({ type: 'BACK' })}
          totalSum={selectedAmount ? String(selectedAmount.fiatAmount) : ''}
          currencySign={selectedAmount?.currencySign}
          onClose={onClose}
        />
      );
    }

    if (state.matches('error')) {
      return <Error onClose={onClose} onGoBackSelect={() => send({ type: 'BACK' })} />;
    }

    if (state.matches('thankYou')) {
      return (
        <FinalScreen
          productId={state.context?.productId}
          resultBalance={String(userData?.balance) || '0'}
          amountPurchased={String(state.context?.amount) || '0'}
          onClose={onClose}
        />
      );
    }
  }, [onClose, send, state, showBack, onBack, userData]);

  return <>{renderStep()}</>;
}
